import type {
  BlockItem,
  DashboardConfig,
  LibraryWidgetConfig,
  WidgetConfig,
  WidgetRef,
} from '../types/dashboard-config.js';

// Saved-widget pointers (`library_widget`) and the legacy types they replace. Pure; shared by the
// builders (chat-components) and both backends (via backend-core).

const LEGACY_LIBRARY_TYPES: ReadonlySet<string> = new Set(['seg_widget', 'pj_widget', 'pj_media']);

export type LibraryWidget = Extract<WidgetConfig, { type: 'library_widget' }>;

/** True for any saved-widget pointer, current or legacy. */
export function isLibraryWidgetType(type: string): boolean {
  return type === 'library_widget' || LEGACY_LIBRARY_TYPES.has(type);
}

/** The library and id a pointer resolves against; a pointer without `source` is a project-library one. */
export function widgetRefOf(config: Pick<LibraryWidgetConfig, 'widgetId' | 'source'>): WidgetRef {
  return { source: config.source ?? 'project_library', widgetId: config.widgetId };
}

/** The pointer a widget holds, or null for inline (non-library) widgets. */
export function widgetRefOfWidget(widget: { type: string; config: object }): WidgetRef | null {
  if (!isLibraryWidgetType(widget.type)) return null;
  const config = widget.config as { widgetId?: unknown; source?: LibraryWidgetConfig['source'] };
  if (typeof config.widgetId !== 'number') return null;
  return widgetRefOf({ widgetId: config.widgetId, source: config.source });
}

function upgradeWidget(widget: WidgetConfig): WidgetConfig {
  if (widget.type !== 'seg_widget' && widget.type !== 'pj_widget' && widget.type !== 'pj_media') return widget;
  const { title, widgetId } = widget.config;
  // A legacy pj_media pointer could hold id 0, which library_widget rejects; leave it as is (it
  // still validates and renders as missing) rather than make the page unsavable.
  if (!Number.isInteger(widgetId) || widgetId <= 0) return widget;
  return { ...widget, type: 'library_widget', config: { title, widgetId, source: 'project_library' } };
}

function upgradeItem(item: BlockItem): BlockItem {
  if (item.type === 'widget') {
    const widget = upgradeWidget(item.widget);
    return widget === item.widget ? item : { ...item, widget };
  }
  const widgets = item.section.widgets.map((w) => {
    const widget = upgradeWidget(w.widget);
    return widget === w.widget ? w : { ...w, widget };
  });
  const changed = widgets.some((w, i) => w !== item.section.widgets[i]);
  return changed ? { ...item, section: { ...item.section, widgets } } : item;
}

/**
 * Rewrites the legacy pointer types (`seg_widget`, `pj_widget`, `pj_media`) to `library_widget`
 * with `source: 'project_library'`. Run on load, so the next save stores the new type; no data
 * migration. Returns the same object when nothing changed.
 */
export function upgradeLegacyLibraryRefs(config: DashboardConfig): DashboardConfig {
  const blocks = config.blocks.map((block) => {
    const items = block.items.map(upgradeItem);
    return items.every((item, i) => item === block.items[i]) ? block : { ...block, items };
  });
  const changed = blocks.some((block, i) => block !== config.blocks[i]);
  return changed ? { ...config, blocks } : config;
}

/** Every widget on a page, in block → item → section order. */
export function pageWidgets(config: DashboardConfig): WidgetConfig[] {
  return config.blocks.flatMap((block) =>
    block.items.flatMap((item) => (item.type === 'widget' ? [item.widget] : item.section.widgets.map((w) => w.widget))),
  );
}
